import React, { useEffect, useState } from 'react';
import type { AppContext } from '../app';
import type { GestureEvent } from '../director/Director';

interface Props {
  ctx: AppContext;
  seed?: string | undefined;
}

const BACKUP_DISMISSED_KEY = 'alds-backup-acknowledged-v1';

export function Inspector({ ctx, seed }: Props): JSX.Element {
  const [, force] = useState({});
  const [showSeed, setShowSeed] = useState(false);
  const [copied, setCopied] = useState(false);
  /**
   * U1 — BACK UP SEED warning is critical for new users but takes real
   * estate on every screen. Once the user has tapped "I've backed it up"
   * we suppress the prominent panel — the seed is still recoverable via
   * the More tab → Export account flow. Tracked in localStorage so it
   * survives session restarts.
   */
  const [backupAcknowledged, setBackupAcknowledged] = useState<boolean>(() => {
    try { return localStorage.getItem(BACKUP_DISMISSED_KEY) !== null; }
    catch { return false; }
  });
  const acknowledgeBackup = (): void => {
    try { localStorage.setItem(BACKUP_DISMISSED_KEY, new Date().toISOString()); } catch { /* noop */ }
    setBackupAcknowledged(true);
  };

  useEffect(() => {
    return ctx.director.subscribe(() => force({}));
  }, [ctx]);

  const state = ctx.director.state();
  const log = ctx.director.log();

  return (
    <aside className="inspector">
      <style>{styles}</style>

      {seed && !backupAcknowledged && (
        <div className="panel schematic-frame backup-warning">
          <div className="panel-title">
            <span>⚠ back up seed</span>
            <span>{showSeed ? 'visible' : 'hidden'}</span>
          </div>
          <p className="warn-text">
            Save this seed offline. There is no recovery if you lose it.
          </p>
          {showSeed ? (
            <>
              <div className="seed-display">{seed}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <button
                  className="pill"
                  onClick={() => {
                    void (async () => {
                      try {
                        await navigator.clipboard.writeText(seed);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      } catch { /* clipboard unavailable */ }
                    })();
                  }}
                >{copied ? '✓ copied' : 'copy seed'}</button>
                <button className="pill" onClick={() => setShowSeed(false)}>hide</button>
                <button className="pill" onClick={acknowledgeBackup}>i&apos;ve backed it up</button>
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="cta" onClick={() => setShowSeed(true)}>
                reveal seed
              </button>
              <button className="pill" onClick={acknowledgeBackup}>i&apos;ve backed it up</button>
            </div>
          )}
        </div>
      )}

      <div className="panel schematic-frame">
        <div className="panel-title">
          <span>director · fsm state</span>
          <span>●</span>
        </div>
        <div className="state-rows">
          <Row label="active" value={state.active ?? '—'} />
          <Row label="stack" value={String(state.stack.length)} />
          <Row label="ambient" value={String(state.ambient.length)} />
          <Row label="gestures" value={String(log.length)} />
        </div>
      </div>

      <div className="panel schematic-frame">
        <div className="panel-title">
          <span>gesture log</span>
          <span>{log.length}</span>
        </div>
        <div className="gesture-log">
          {log.length === 0 && <div className="empty">awaiting first gesture…</div>}
          {[...log].reverse().slice(0, 30).map((ev, i) => (
            <GestureRow key={log.length - i} ev={ev} />
          ))}
        </div>
      </div>

      <div className="panel schematic-frame">
        <div className="panel-title">
          <span>transition grammar</span>
        </div>
        <div className="grammar">
          <GrammarRow name="push" desc="deepen flow" />
          <GrammarRow name="pop" desc="back out" />
          <GrammarRow name="lift" desc="raise diminished" />
          <GrammarRow name="spawn" desc="transient over env" />
          <GrammarRow name="bridge" desc="cross-env transition" />
          <GrammarRow name="interrupt" desc="priority preempt" />
        </div>
      </div>

      <SyncPanel ctx={ctx} />
      <IdentityPanel ctx={ctx} />
    </aside>
  );
}

/**
 * SyncPanel — live diagnostics for the mesh node + sync provider.
 *
 * Shows uptime, peer count, and the last replication round timestamp.
 * Empty/zeros when running on LocalSyncProvider + MockMeshNode (the alpha
 * default); becomes meaningful when OrbitDB is wired in.
 */
function SyncPanel({ ctx }: { ctx: AppContext }): JSX.Element {
  const [stats, setStats] = useState<{ peerId: string; connected: number; uptime: number; lastSync: string }>({
    peerId: '—', connected: 0, uptime: 0, lastSync: 'never',
  });

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await ctx.meshNode.stats();
        if (cancelled) return;
        setStats({
          peerId: s.peerId.length > 12 ? s.peerId.slice(0, 6) + '…' + s.peerId.slice(-4) : s.peerId,
          connected: s.connected,
          uptime: Math.floor(s.uptime / 1000),
          lastSync: s.lastSyncAt
            ? `${Math.floor((Date.now() - s.lastSyncAt) / 1000)}s ago`
            : 'never',
        });
      } catch { /* node not started */ }
    };
    void tick();
    const interval = setInterval(() => { void tick(); }, 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [ctx]);

  return (
    <div className="panel schematic-frame">
      <div className="panel-title">
        <span>mesh · sync</span>
        <span>{stats.connected > 0 ? '◉ live' : '○ alone'}</span>
      </div>
      <div className="state-rows">
        <Row label="peer" value={stats.peerId} />
        <Row label="connected" value={String(stats.connected)} />
        <Row label="uptime" value={`${stats.uptime}s`} />
        <Row label="last sync" value={stats.lastSync} />
      </div>
    </div>
  );
}

/**
 * IdentityPanel — model-aware diagnostics.
 *
 * Both repos compile this code (the UI is shared). In the global build,
 * the linkage-proof button is shown as "trivial" because pubkeys are
 * already identical. In the hierarchical build, it actually exercises
 * the zk-linkage path and surfaces the proof bytes.
 */
function IdentityPanel({ ctx }: { ctx: AppContext }): JSX.Element {
  const [proofResult, setProofResult] = useState<{ aHash: string; bHash: string; bytes: number; zk: boolean } | null>(null);

  const runDemo = async () => {
    const proof = await ctx.identity.proveLinkage(
      { environment: 'chat', purpose: 'demo-a' },
      { environment: 'vault', purpose: 'demo-b' },
    );
    setProofResult({
      aHash: proof.ctxA.slice(0, 12),
      bHash: proof.ctxB.slice(0, 12),
      bytes: proof.proof.length,
      zk: proof.zeroKnowledge,
    });
  };

  return (
    <div className="panel schematic-frame">
      <div className="panel-title">
        <span>identity · {ctx.identityModel}</span>
        <span>{(ctx.identityModel as string) === 'global' ? '∅ correlation' : '⌬ derived'}</span>
      </div>
      <p className="warn-text" style={{ fontSize: 10, color: 'var(--ink-dim)' }}>
        {(ctx.identityModel as string) === 'global'
          ? 'One key signs all contexts. Linkage is implicit.'
          : 'Per-context derived keys. Linkage requires explicit proof.'}
      </p>
      <button className="cta secondary" style={{ fontSize: 9 }} onClick={() => void runDemo()}>
        demo linkage proof
      </button>
      {proofResult && (
        <div style={{ marginTop: 10, fontSize: 10, color: 'var(--ink-dim)' }}>
          <Row label="ctx_a" value={proofResult.aHash + '…'} />
          <Row label="ctx_b" value={proofResult.bHash + '…'} />
          <Row label="proof" value={`${proofResult.bytes}B`} />
          <Row label="zk" value={proofResult.zk ? 'yes' : 'no'} />
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="row">
      <span className="key">{label}</span>
      <span className="val">{value}</span>
    </div>
  );
}

function GestureRow({ ev }: { ev: GestureEvent }) {
  return (
    <div className="log-row">
      <span className="log-time">+{((Date.now() - ev.at) / 1000).toFixed(0)}s</span>
      <span className={`log-gesture g-${ev.gesture}`}>{ev.gesture}</span>
      <span className="log-detail">{ev.from ?? '∅'} → {ev.to}</span>
    </div>
  );
}

function GrammarRow({ name, desc }: { name: string; desc: string }) {
  return (
    <div className="grammar-row">
      <span className="g-name">{name}</span>
      <span className="g-desc">{desc}</span>
    </div>
  );
}

const styles = `
.inspector {
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-size: 11px;
  overflow-y: auto;
}

/* U1.4 — hide Inspector chrome (DIRECTOR FSM state, gesture log,
 * transition grammar) on mobile. These panels are useful debug tools
 * for operators on a desktop dev session but show up as dev clutter
 * underneath surface content on mobile. The BACK UP SEED warning is
 * the one exception — it's surfaced separately at app-shell level
 * on mobile via the MobileBackupBanner component. */
@media (max-width: 900px) {
  .inspector { display: none; }
}

.panel {
  background: rgba(17, 22, 26, 0.5);
  padding: 16px;
}
.panel-title {
  font-size: 9px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--ink-dim);
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  border-bottom: 1px dashed var(--rule);
  padding-bottom: 8px;
}
.panel-title span:last-child { color: var(--phos); }

.backup-warning {
  border-color: var(--amber);
}
.backup-warning .panel-title { color: var(--amber); }
.warn-text {
  font-size: 11px;
  line-height: 1.6;
  color: var(--ink);
  margin: 0 0 12px;
}
.seed-display {
  font-family: var(--mono);
  font-size: 11px;
  word-break: break-all;
  padding: 10px;
  background: var(--bg);
  border: 1px solid var(--rule);
  color: var(--amber);
}

.state-rows .row, .row {
  display: flex;
  justify-content: space-between;
  padding: 3px 0;
}
.row .key {
  color: var(--ink-dim);
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.row .val {
  color: var(--phos);
  font-weight: 500;
  font-size: 10px;
}

.gesture-log {
  max-height: 220px;
  overflow-y: auto;
  font-size: 10px;
}
.gesture-log .empty {
  color: var(--ink-dim);
  font-style: italic;
}
.log-row {
  display: grid;
  grid-template-columns: 44px 70px 1fr;
  gap: 8px;
  padding: 2px 0;
  font-size: 9px;
}
.log-time { color: var(--ink-dim); }
.log-gesture {
  font-weight: 500;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}
.log-detail { color: var(--ink-dim); }
.g-push    { color: var(--phos); }
.g-pop     { color: var(--ink-dim); }
.g-lift    { color: var(--phos); }
.g-spawn   { color: var(--amber); }
.g-bridge  { color: var(--blue); }
.g-interrupt { color: var(--red); }

.grammar { font-size: 10px; line-height: 1.8; }
.grammar-row {
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 10px;
  padding: 3px 0;
}
.g-name {
  color: var(--phos);
  font-size: 9px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
}
.g-desc { color: var(--ink-dim); }
`;
